import { UseFormReturn } from "react-hook-form";
import { useContext, useState } from "react";
import classNames from "classnames";
import { isEmpty } from "lodash";

import { Typography } from "@/components/Typography";
import { Form } from "@/components/Form";

import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { QREditorContext } from "@/contexts/QREditorContext";

import { applyQRFormDefaultValues, onBindClick } from "@/utils/formHelper";
import { mp3Validation } from "@/utils/validators/qr-mp3";
import {
  isFileAblob,
  onBindImage,
  onBindSocialNetworks,
  onBindThemePicker,
} from "@/utils/qrEditorHelper";

import { QRMP3Props } from "./types";
import { FileProps } from "@/components/FilesUpload/types";

import { NAMESPACES } from "@/internals/constants/namespaces";
import { useTranslation } from "react-i18next";
import "./styles.scss";

export const QRMp3 = ({ data }: QRMP3Props) => {
  const { t } = useTranslation(NAMESPACES.VALIDATIONS);
  const { qrData, setQrData } = useContext<QREditorContextProps<"mp3">>(QREditorContext);

  const [buttonFieldsVisible, setButtonFieldsVisible] = useState(!!qrData.data.mp3ButtonUrl);
  const [methods, setMethods] = useState<UseFormReturn>(null);

  const classnames = classNames(`qr-mp3`, {
    "button-fields-visible": buttonFieldsVisible,
  });

  const dataMp3WithDefaultValues = applyQRFormDefaultValues(data, qrData.data);

  const omittedValues =
    isEmpty(qrData.data.mp3FileUrl) || isFileAblob(qrData.data.mp3FileUrl)
      ? ["data.mp3FileUrl"]
      : [];

  onBindImage(dataMp3WithDefaultValues, "mp3InformationLogo", omittedValues);

  onBindClick(dataMp3WithDefaultValues, "mp3ButtonAdd", () => {
    methods?.trigger("mp3ButtonText");
    methods?.trigger("mp3ButtonUrl");

    setButtonFieldsVisible(true);
  });

  onBindClick(dataMp3WithDefaultValues, "mp3ButtonRemove", () => {
    setQrData(
      {
        ...qrData,
        data: {
          ...qrData.data,
          mp3ButtonText: "",
          mp3ButtonUrl: "",
        },
      },
      omittedValues
    );

    methods?.resetField("mp3ButtonText", { keepError: true });
    methods?.trigger("mp3ButtonText");

    methods?.resetField("mp3ButtonUrl", { keepError: true });
    methods?.trigger("mp3ButtonUrl");

    setButtonFieldsVisible(false);
  });

  onBindClick(dataMp3WithDefaultValues, "mp3Url", (files: FileProps[]) => {
    setQrData(
      {
        ...qrData,
        data: {
          ...qrData.data,
          mp3FileUrl: files[0]?.url as string,
          mp3FileName: files[0]?.name,
        },
      },
      omittedValues
    );
  });

  onBindSocialNetworks(dataMp3WithDefaultValues, "mp3Socials", "socialNetworks", omittedValues);

  onBindThemePicker(dataMp3WithDefaultValues, "themeColor", omittedValues);

  const onUpdateState = ({ field, value, component }) => {
    if (field === "file" || field == "") return;
    setQrData(
      {
        ...qrData,
        data: {
          ...qrData.data,
          [field]: field === "allowDownload" ? component.checked : value,
        },
      },
      omittedValues
    );
  };

  return (
    <div className={classnames}>
      <Typography
        Tag="p"
        size="xl"
      >
        {data.title}
      </Typography>
      <Form
        fields={dataMp3WithDefaultValues}
        schema={mp3Validation(data, methods, t)}
        onInit={(methods) => setMethods(methods)}
        onWatchFields={({ field, value, component }) => onUpdateState({ field, value, component })}
      />
    </div>
  );
};
